// SplitRecordsFollowUpDialog
//
// "Which of their records should follow them?" — the step that runs
// immediately after a person is split into their own household
// (Joy + Brandon, 2026-08-19).
//
// ── Why this exists ──────────────────────────────────────────────────
// Splitting a contact out moved the PERSON and nothing else. Their own
// auto policy stayed on the household they left. The old confirm dialog
// said so plainly — "moving those over is a separate step" — but being
// honest about it didn't make it less broken. For the case Joy actually
// described, a young adult who owns and manages their own policy, the
// split produced a household with a person and no records beside a
// household holding records for someone who no longer lived there. That
// is worse than leaving them put, which is almost certainly why nobody
// used the feature.
//
// So the "separate step" happens here, one screen later, with the
// records already found and listed — instead of sending someone off to
// hunt for them by hand and, realistically, never doing it.
//
// Reused by ReorganizeMembersModal for a GROUP of movers (2026-09-05):
// `personName` can be a joined list ("Steve & Mary Zins") and `records`
// is computed against the whole moving group, so a record naming only
// people who all left together still shows as solely theirs.
//
// ── What it will and won't pre-check ─────────────────────────────────
// Only records where the departing person is the SOLE named party are
// pre-checked. A record naming two or more people is a JOINT record —
// a policy covering both a parent and a child — and moving it would
// strand the people left behind. Those are listed, unchecked, with the
// other names shown, so the choice is visible rather than hidden.
//
// ── Reversible, one record at a time ─────────────────────────────────
// Each move is its own `moveRecordToHousehold` call, which is already a
// reversible bulk action. Moving them individually rather than as one
// batch means a partial failure leaves a coherent state — the records
// that moved, moved — instead of an ambiguous half-applied batch. Skip
// is always a first-class answer: nothing here is required, and the
// records can be moved later from the record itself.

import { useState } from "react";

import { api, type SplitMovableRecord } from "../api";
import { Button } from "../ui";
import { Modal } from "./Modal";
import { useToast } from "./Toast";

/** Canonical reason code for a record following a household split. */
const SPLIT_REASON_CODE = "household_split";

const KIND_LABEL: Record<SplitMovableRecord["kind"], string> = {
  service_record: "Service",
  opportunity: "Opportunity",
  transaction: "Processing",
};

export interface SplitRecordsFollowUpDialogProps {
  /** Person who just moved out. */
  personName: string;
  /** Household they landed in. */
  newHouseholdId: string;
  newHouseholdName: string;
  /** Records still on the old household that name them. */
  records: SplitMovableRecord[];
  /**
   * The split's own bulk action. Each move JOINS it rather than opening
   * its own, so undoing the split brings these records back with the
   * person instead of needing one undo per record.
   */
  bulkActionId?: string | null;
  /** Closes the dialog — also the "Skip" path. */
  onClose: () => void;
  /** Fires after at least one record moved, so the page can refetch. */
  onMoved: () => void;
}

export function SplitRecordsFollowUpDialog({
  personName,
  newHouseholdId,
  newHouseholdName,
  records,
  bulkActionId,
  onClose,
  onMoved,
}: SplitRecordsFollowUpDialogProps) {
  const toast = useToast();
  const [running, setRunning] = useState(false);
  // Solely-owned records start checked; joint ones never do — see header.
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(records.filter((r) => r.is_only_person).map((r) => r.id)),
  );

  function toggle(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function moveSelected(): Promise<void> {
    const chosen = records.filter((r) => selected.has(r.id));
    if (chosen.length === 0) {
      onClose();
      return;
    }
    setRunning(true);
    let moved = 0;
    const failures: string[] = [];
    for (const record of chosen) {
      try {
        await api.moveRecordToHousehold(record.kind, record.id, {
          target_household_id: newHouseholdId,
          reason_code: SPLIT_REASON_CODE,
          reason: `Followed ${personName} to ${newHouseholdName}`,
          ...(bulkActionId ? { bulk_action_id: bulkActionId } : {}),
        });
        moved += 1;
      } catch (err) {
        failures.push(`${record.label}: ${err instanceof Error ? err.message : "failed"}`);
      }
    }
    setRunning(false);

    // Report what actually happened, both halves. A partial result that
    // reads as a clean success is how someone later finds a record
    // sitting where they were told it no longer was.
    if (moved > 0) {
      toast.success(`Moved ${moved} record${moved === 1 ? "" : "s"} to ${newHouseholdName}.`);
      onMoved();
    }
    if (failures.length > 0) {
      toast.error(
        `${failures.length} record${failures.length === 1 ? "" : "s"} couldn't be moved — ${failures[0]}`,
      );
    }
    if (failures.length === 0) onClose();
  }

  const jointCount = records.filter((r) => !r.is_only_person).length;

  return (
    <Modal
      open
      onClose={running ? () => {} : onClose}
      title={`Should any records follow ${personName}?`}
    >
      <p style={{ marginTop: 0 }}>
        {personName} is now in <strong>{newHouseholdName}</strong>. These records on the old
        household name them. Moving one brings it onto their household; each move is reversible.
      </p>

      <ul style={{ listStyle: "none", margin: "12px 0", padding: 0 }}>
        {records.map((record) => (
          <li key={record.id} style={{ marginBottom: 10 }}>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <input
                type="checkbox"
                checked={selected.has(record.id)}
                disabled={running}
                onChange={() => toggle(record.id)}
                style={{ marginTop: 3 }}
              />
              <span>
                <strong>{record.label}</strong>{" "}
                <span style={{ opacity: 0.7 }}>
                  ({KIND_LABEL[record.kind]}
                  {record.company_slug ? ` · ${record.company_slug}` : ""}
                  {record.status ? ` · ${record.status}` : ""})
                </span>
                {!record.is_only_person && (
                  <div style={{ fontSize: 13, opacity: 0.8, marginTop: 2 }}>
                    Also names {record.other_person_names.join(", ")} — moving it takes it away from
                    them too.
                  </div>
                )}
              </span>
            </label>
          </li>
        ))}
      </ul>

      {jointCount > 0 && (
        <p style={{ fontSize: 13, opacity: 0.85 }}>
          {jointCount === 1 ? "One record is" : `${jointCount} records are`} shared with someone
          still in the old household, so {jointCount === 1 ? "it is" : "they are"} left unchecked.
        </p>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <Button variant="sw-default" onClick={onClose} disabled={running}>
          Skip for now
        </Button>
        <Button variant="sw-navy" onClick={() => void moveSelected()} disabled={running}>
          {running
            ? "Moving…"
            : selected.size === 0
              ? "Move nothing"
              : `Move ${selected.size} record${selected.size === 1 ? "" : "s"}`}
        </Button>
      </div>
    </Modal>
  );
}
